import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { EMPTY, catchError, expand, from, map, mergeMap, Observable, of, reduce, toArray } from 'rxjs';
import {
  ApiResponse,
  FlowFormFieldBuilder,
  FlowFormResponse,
  FlowFormResponseSend,
  FlowFormSummaryResponse,
  FlowFormTemplateResponse,
  mapFieldLabels,
  PaginatedFlowFormsResponse,
  ProductsFlowsFullAPIResponse,
  PublicFormResponse,
  PublicFormSectionPathResponse,
  PublicFormSubmitResponse,
  SyncFlowFormRequest,
  SummaryCostResponse,
  TableRowsQuery,
  UploadedFieldFile,
  validateFormResponse
} from 'src/core/interfaces/flowForms.interfaces';
import { TableRowsPage } from 'src/core/interfaces/base-form.interface';
import { ApplicantType } from '../flow-form-fields/flow-form-fields.interface';

const SUMMARY_PRODUCT_CHUNK_SIZE = 50;
const SUMMARY_PAGE_SIZE = 100;
const SUMMARY_MAX_CONCURRENT_REQUESTS = 4;

@Injectable({
  providedIn: 'root',
})
export class FlowFormsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.prequalificationFlowAPI}/flow-forms`;
  private readonly formResponsesUrl = `${environment.prequalificationFlowAPI}/flow-form-responses`;
  private readonly publicFormsUrl = `${environment.prequalificationFlowAPI}/public/forms`;

  getFormSummariesByProductIds(productIds: number[]): Observable<FlowFormSummaryResponse[]> {
    if (!productIds.length) return of([]);

    const chunks: number[][] = [];
    for (let i = 0; i < productIds.length; i += SUMMARY_PRODUCT_CHUNK_SIZE) {
      chunks.push(productIds.slice(i, i + SUMMARY_PRODUCT_CHUNK_SIZE));
    }

    return from(chunks).pipe(
      mergeMap(
        (chunk) => this.fetchAllSummaryPages(chunk).pipe(catchError(() => of([] as FlowFormSummaryResponse[]))),
        SUMMARY_MAX_CONCURRENT_REQUESTS,
      ),
      toArray(),
      map((pages) => pages.flat()),
      catchError(() => of([])),
    );
  }

  private fetchAllSummaryPages(productIds: number[]): Observable<FlowFormSummaryResponse[]> {
    return this.fetchSummaryPage(productIds, 1).pipe(
      expand((response) =>
        (response.data.currentPage ?? 1) < (response.data.totalPages ?? 1)
          ? this.fetchSummaryPage(productIds, (response.data.currentPage ?? 1) + 1)
          : EMPTY,
      ),
      reduce((items, response) => [...items, ...(response.data.items ?? [])], [] as FlowFormSummaryResponse[]),
    );
  }

  private fetchSummaryPage(productIds: number[], page: number): Observable<PaginatedFlowFormsResponse> {
    let params = new HttpParams().set('page', String(page)).set('limit', String(SUMMARY_PAGE_SIZE));
    productIds.forEach((id) => {
      params = params.append('productIds', id.toString());
    });
    return this.http.get<PaginatedFlowFormsResponse>(this.baseUrl, { params });
  }

  uploadFormImage(productId: number, imageFile: File): Observable<string> {
    const formData = new FormData();
    formData.append('image', imageFile);

    return this.http.post<ApiResponse<{ imageUrl: string }>>(
      `${this.baseUrl}/products/${productId}/upload-image`,
      formData
    ).pipe(
      map(response => response.data.imageUrl),
      catchError(error => {
        console.error('Error uploading image:', error);
        throw error;
      })
    );
  }

  syncFlowForms(
    productId: number,
    request: SyncFlowFormRequest,
    isAutoSave: boolean = false
  ): Observable<FlowFormResponse[]> {
    let params = new HttpParams();

    if (isAutoSave) {
      params = params.set('autosave', 'true');
    }

    return this.http.post<ApiResponse<FlowFormResponse[]>>(
      `${this.baseUrl}/products/${productId}/sync`,
      request,
      { params }
    ).pipe(
      map(response => response.data)
    );
  }

  cloneFlowForms(
    sourceProductId: number,
    targetProductId: number
  ): Observable<FlowFormResponse[]> {
    return this.http.post<ApiResponse<FlowFormResponse[]>>(
      `${this.baseUrl}/products/${targetProductId}/clone`,
      {
        sourceProductId,
        targetProductId
      }
    ).pipe(
      map(response => response.data)
    );
  }

  getFormByVersion(formCode: string, version: number): Observable<FlowFormResponse> {
    return this.http
      .get<ApiResponse<FlowFormResponse>>(`${this.baseUrl}/code/${encodeURIComponent(formCode)}/versions/${version}`)
      .pipe(map((response) => ({
        ...response.data,
        sections: (response.data.sections ?? []).map((section) => ({ ...section, fields: section.fields.map(mapFieldLabels) })),
      })));
  }

  getFlowFormsByProductId(productId: number, status?: string[], isActive?: boolean): Observable<FlowFormResponse[]> {
    let params = new HttpParams();

    if (status && status.length > 0) {
      status.forEach(s => {
        params = params.append('status', s);
      });
    }
    if(isActive !== undefined) {
      params = params.set('isActive', isActive.toString());
    }
    
    return this.http.get<ApiResponse<FlowFormResponse[]>>(
      `${this.baseUrl}/products/${productId}`,
      { params }
    ).pipe(
      map(response => {
        return response.data.map(form => ({
          ...form,
          sections: form.sections.map(section => ({
            ...section,
            fields: section.fields
              .filter((f, i, arr) => {
                const key = (f as any).fieldId || (f as any).fieldInstanceId || f.originalName;
                return arr.findIndex(x => ((x as any).fieldId || (x as any).fieldInstanceId || x.originalName) === key) === i;
              })
              .map(mapFieldLabels)
          }))
        }));
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  generatePublicFormToken(
    formId: string,
    body: {
      tokenType: string;
      usageType: string;
      allowedDomains?: string[];
      expiresAt?: string | null;
    }
  ): Observable<string> {
    return this.http
      .post<ApiResponse<{ token: string }>>(
        `${this.baseUrl}/${formId}/tokens`,
        body
      )
      .pipe(
        map((response) => response.data.token),
        catchError((error) => {
          throw error;
        })
      );
  }
  
  generateFlowFormTemplateByProductId(
    productId: number,
    applicantType?: ApplicantType
  ): Observable<FlowFormTemplateResponse> {
    let params = new HttpParams();

    if (applicantType) {
      params = params.set('applicantType', applicantType);
    }

    return this.http.get(
      `${this.baseUrl}/products/${productId}/template`,
      {
        params,
        responseType: 'blob',
        observe: 'response',
      }
    ).pipe(
      map(response => {
        const contentDisposition = response.headers.get('content-disposition');

        return {
          filename: this.extractFilename(contentDisposition) ?? 'Plantilla.xlsx',
          contentType: response.headers.get('content-type') || 'application/octet-stream',
          data: response.body as Blob,
        }
      }),
      catchError((error: HttpErrorResponse) => {
        throw error;
      })
    );
  }

  getAvailableFieldsForBuilder(
    productId: number,
    applicantType: 'individual' | 'legal_entity' ,
    search?: string
  ): Observable<FlowFormFieldBuilder[]> {
    let params = new HttpParams().set('applicantType', applicantType);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<any>(
      `${environment.prequalificationFlowAPI}/products/${productId}/flow-form-fields/builder`,
      { params }
    ).pipe(
      map(response => {
        const data = response.data && Array.isArray(response.data)
          ? response.data
          : response;

        return data.map(mapFieldLabels);
      })
    );
  }

  getProductsFlows({
    search,
    pageNumber,
    pageSize,
    orderBy,
    orderDirection,
    status,
  }: {
    search: string | null;
    pageNumber: number;
    pageSize: number;
    orderBy?: string[];
    orderDirection?: string[];
    status?: string[] | null;
  }): Observable<ProductsFlowsFullAPIResponse<FlowFormResponse>> {
    let params = new HttpParams()
      .set('page', pageNumber.toString())
      .set('limit', pageSize.toString());

    if ((orderBy ?? []).length > 0) {
      params = params.set('orderBy', (orderBy ?? []).join(','));
    }

    if ((orderDirection ?? []).length > 0) {
      params = params.set('orderDirection', (orderDirection ?? []).join(','));
    }

    if (search) {
      params = params.set('search', search);
    }
    if ((status ?? []).length > 0) {
      (status ?? []).forEach((s) => {
        params = params.append('status', s);
      });
    }

    const url = `${this.baseUrl}/products-search`;
    return this.http.get<ProductsFlowsFullAPIResponse<FlowFormResponse>>(url, {
      params,
    });
  }

  sendFormById(
    formId: string | null,
    data: FlowFormResponseSend | null
  ): Observable<ProductsFlowsFullAPIResponse<FlowFormResponse>> {
    return this.http
      .post<ProductsFlowsFullAPIResponse<FlowFormResponse>>(
        `${environment.prequalificationFlowAPI}/flow-form-responses/forms/${formId}/submit`,
        data ?? {}
      )
      .pipe(
        catchError((error) => {
          throw error;
        })
      );
  }


  getPublicFormByToken(token: string): Observable<PublicFormResponse> {
    return this.http
      .get<ApiResponse<PublicFormResponse>>(
        `${environment.prequalificationFlowAPI}/public/forms/${token}`
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          throw error;
        })
      );
  }

  submitPublicFormByToken(
    token: string,
    data: FlowFormResponseSend
  ): Observable<ApiResponse<PublicFormSubmitResponse>> {
    return this.http
      .post<ApiResponse<PublicFormSubmitResponse>>(
        `${environment.prequalificationFlowAPI}/public/forms/${token}/submit`,
        data
      )
      .pipe(
        catchError((error) => {
          throw error;
        })
      );
  }

  getPublicFormSectionPath(
    token: string,
    accumulatedAnswers: Pick<FlowFormResponseSend, 'sections'>
  ): Observable<PublicFormSectionPathResponse> {
    return this.http
      .post<ApiResponse<PublicFormSectionPathResponse>>(
        `${environment.prequalificationFlowAPI}/public/forms/${token}/section-path`,
        accumulatedAnswers
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          throw error;
        })
      );
  }

  getFormSectionPath(
    formId: string,
    accumulatedAnswers: Pick<FlowFormResponseSend, 'sections'>
  ): Observable<PublicFormSectionPathResponse> {
    return this.http
      .post<ApiResponse<PublicFormSectionPathResponse>>(
        `${this.baseUrl}/${encodeURIComponent(formId)}/section-path`,
        accumulatedAnswers
      )
      .pipe(map((response) => response.data));
  }

  uploadFormFieldDocuments(
    formId: string,
    fieldName: string,
    files: readonly File[],
    clientIdentification?: string
  ): Observable<UploadedFieldFile[]> {
    const params = clientIdentification
      ? new HttpParams().set('clientIdentification', clientIdentification)
      : undefined;
    return this.postFieldFiles(
      `${this.formResponsesUrl}/forms/${encodeURIComponent(formId)}/fields/${encodeURIComponent(fieldName)}/documents`,
      files,
      params
    );
  }

  uploadFormFieldSignature(
    formId: string,
    fieldName: string,
    image: File
  ): Observable<UploadedFieldFile[]> {
    return this.postFieldFiles(
      `${this.formResponsesUrl}/forms/${encodeURIComponent(formId)}/fields/${encodeURIComponent(fieldName)}/signature`,
      [image]
    );
  }

  uploadPublicFormFieldDocuments(
    token: string,
    fieldName: string,
    files: readonly File[]
  ): Observable<UploadedFieldFile[]> {
    return this.postFieldFiles(
      `${this.publicFormsUrl}/${encodeURIComponent(token)}/fields/${encodeURIComponent(fieldName)}/documents`,
      files
    );
  }

  uploadPublicFormFieldSignature(
    token: string,
    fieldName: string,
    image: File
  ): Observable<UploadedFieldFile[]> {
    return this.postFieldFiles(
      `${this.publicFormsUrl}/${encodeURIComponent(token)}/fields/${encodeURIComponent(fieldName)}/signature`,
      [image]
    );
  }

  getFormFieldTableRows(formId: string, fieldName: string, query: TableRowsQuery): Observable<TableRowsPage> {
    return this.postTableRows(
      `${this.baseUrl}/${encodeURIComponent(formId)}/fields/${encodeURIComponent(fieldName)}/table-rows`,
      query
    );
  }

  getPublicFormFieldTableRows(token: string, fieldName: string, query: TableRowsQuery): Observable<TableRowsPage> {
    return this.postTableRows(
      `${this.publicFormsUrl}/${encodeURIComponent(token)}/fields/${encodeURIComponent(fieldName)}/table-rows`,
      query
    );
  }

  private postTableRows(url: string, query: TableRowsQuery): Observable<TableRowsPage> {
    return this.http
      .post<ApiResponse<TableRowsPage>>(url, query)
      .pipe(map((response) => response.data));
  }

  private postFieldFiles(
    url: string,
    files: readonly File[],
    params?: HttpParams
  ): Observable<UploadedFieldFile[]> {
    const body = new FormData();
    files.forEach((file) => body.append('files', file, file.name));
    return this.http
      .post<ApiResponse<{ files: UploadedFieldFile[] }>>(url, body, { params })
      .pipe(map((response) => response.data?.files ?? []));
  }

  logCaptchaAttempt(payload: {
    origin: 'public_url' | 'embedded_script';
    token: string;
    productId: string;
    expectedAction?: string;
  }): Observable<unknown> {
    const url = `${environment.apiUrl}/common/captcha/attempt`;

    return this.http.post<unknown>(url, payload).pipe(
      catchError((error) => {
        console.error('Error logging captcha attempt:', error);
        throw error;
      })
    );
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    const match = /filename="?([^"]+)"?/.exec(contentDisposition);
    return match?.[1] ?? null;
  }
  
  getCostSummary(productId: string | null): Observable<SummaryCostResponse> {
    return this.http.get<ApiResponse<SummaryCostResponse>>(
      `${environment.prequalificationFlowAPI}/flow/product/${productId}/cost-summary`
    ).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Error fetching cost summary:', error);
        throw error;
      })
    );
  }

  deleteAllByProductId(productId: number): Observable<{ deleted: number }> {
    return this.http
      .delete<ApiResponse<{ deleted: number }>>(`${this.baseUrl}/products/${productId}`)
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          throw error;
        })
      );
  }

  validateForm(productId: string): Observable<validateFormResponse> {
    return this.http.get<ApiResponse<validateFormResponse>>(
      `${this.baseUrl}/products/${productId}/validate`
    ).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Error validating form:', error);
        throw error;
      })
    );
  }
}
